
import { useRef, useEffect, useState, useCallback } from 'react';
import { useIntersectionObserver } from './useIntersectionObserver';

interface UseVideoAutoplayOptions {
  threshold?: number;
  rootMargin?: string;
  playOnIntersect?: boolean;
  pauseOnExit?: boolean;
  disabled?: boolean;
}

export const useVideoAutoplay = (options: UseVideoAutoplayOptions = {}) => {
  const {
    threshold = 0.5,
    rootMargin = '0px',
    playOnIntersect = true,
    pauseOnExit = true
  } = options;

  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  const { targetRef: containerRef, isIntersecting } = useIntersectionObserver({
    onIntersect: () => {}, // No-op, we handle intersection via isIntersecting
    threshold,
    rootMargin
  });

  const playVideo = useCallback(async () => {
    if (videoRef.current && !isPlaying) {
      try {
        await videoRef.current.play();
        setIsPlaying(true);
      } catch (error) {
        console.log('Video autoplay failed:', error);
      }
    }
  }, [isPlaying]);

  const pauseVideo = useCallback(() => {
    if (videoRef.current && isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  }, [isPlaying]);

  // Handle intersection changes
  useEffect(() => {
    if (!videoRef.current) return;

    if (isIntersecting && playOnIntersect && !hasUserInteracted) {
      playVideo();
    } else if (!isIntersecting && pauseOnExit && !hasUserInteracted) {
      pauseVideo();
    }
  }, [isIntersecting, playOnIntersect, pauseOnExit, hasUserInteracted, playVideo, pauseVideo]);

  // Listen for user interactions with video
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleUserPlay = () => {
      setHasUserInteracted(true);
      setIsPlaying(true);
    };

    const handleUserPause = () => {
      setHasUserInteracted(true);
      setIsPlaying(false);
    };

    const handleVideoEnd = () => {
      setIsPlaying(false);
    };

    video.addEventListener('play', handleUserPlay);
    video.addEventListener('pause', handleUserPause);
    video.addEventListener('ended', handleVideoEnd);

    return () => {
      video.removeEventListener('play', handleUserPlay);
      video.removeEventListener('pause', handleUserPause);
      video.removeEventListener('ended', handleVideoEnd);
    };
  }, []);

  return {
    videoRef,
    containerRef,
    isPlaying,
    isIntersecting,
    hasUserInteracted,
    playVideo,
    pauseVideo
  };
};
